<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { RuleBlock } from '@client/components/rule-block';
  import { Switch } from '@client/components/switch';

  import { AssetsService } from '@client/services/assets';

  import { sanitizeRulenameOnKeyUp } from '@client/utils/sanitize';

  import { formatBytes } from '@common/utils/numbers';

  const { addNotification } = getNotificationsContext();

  export let data = {};
  export let invalidRulename = false;

  let currentRulenames = [];
  let files = [];

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: data.filename } }), 'error');

      return;
    }

    const { hash, filesize, filename } = response;

    data = { ...data, hash, filesize, filename };

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: filename } }));
  };

  onMount(async () => {
    const response = await AssetsService.getAllDocuments();

    currentRulenames = response.map(({ rulename }) => rulename);

    currentRulenames = currentRulenames.filter((rulename) => rulename !== data.rulename);
  });

  $: invalidRulename = currentRulenames.includes(data.rulename.toLowerCase());
</script>

<div class="document-edit-form">
  <div class="section pt-3">
    <div class="heading">
      {$format('label.NAME')} <Icon name="help-circle" id="rulename-info" class="cursor-help h5" />
    </div>
    <Tooltip target="rulename-info" placement="top">{$format('label.TOOLTIP.ASSET_DOCUMENT_RULENAME')}</Tooltip>
    <div class="copy-field" class:invalid={!data?.rulename || invalidRulename}>
      <RuleBlock text="[[doc:{data.rulename}]]" />
      <Input
        type="text"
        class="rulename"
        placeholder={$format('label.PLACEHOLDER.INPUT', {
          values: { param: $format('label.NAME').toLowerCase() }
        })}
        invalid={!data?.rulename || invalidRulename}
        feedback={!data.rulename
          ? $format('label.VALIDATION_TOO_SHORT', { values: { param: $format('label.NAME'), length: 1 } })
          : $format('label.VALIDATION_NAME_EXISTS', {
              values: {
                type: $format('label.DOCUMENT'),
                param: $format('label.NAME').toLowerCase()
              }
            })}
        on:keyup={() => (data.rulename = sanitizeRulenameOnKeyUp(data.rulename))}
        bind:value={data.rulename}
      />
    </div>
  </div>
  <div class="section">
    <div class="heading">{$format('label.DOCUMENT_DETAILS')}</div>

    <div class="asset-details">
      <div class="asset-item">
        <div class="document">
          <div class="asset-info">
            <b>{$format('label.PREVIEW')}</b>
            <small>{$format('label.FILE_SIZE')} {formatBytes(data.filesize)}</small>
          </div>
          <div class="asset-actions">
            <img
              src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              style="background: url(https://www.sendergen.com/v2/view/{data.hash}/thumbnail.gif) no-repeat center center;"
              alt={data.filename}
              loading="lazy"
            />
          </div>
        </div>
      </div>
      <div class="asset-item">
        <div class="file">
          <div class="asset-info">
            <b>{$format('label.UPLOAD_FILE')}</b>
            <p>{$format('label.DESCRIPTION.ASSET_DOCUMENT_UPLOAD_FILE')}</p>
          </div>
          <div class="asset-actions">
            <label class="btn btn-secondary btn-sm" for="document">{$format('label.CHOOSE_FILE')}</label>
            <span id="file-chosen">
              {#if data.filename}
                {data.filename} ({formatBytes(data.filesize)})
              {:else}
                {$format('label.NO_FILE_CHOSEN')}
              {/if}
            </span>
            <input id="document" name="document" hidden type="file" bind:files on:change={onFileUpload} />
          </div>
        </div>
      </div>
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.VIEW_ONLY')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_DOCUMENT_OPTION_VIEW_ONLY')}</p>
        </div>
        <div class="asset-actions">
          <Switch bind:checked={data.viewonly} />
        </div>
      </div>
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.NON_COMPLIANT')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_DOCUMENT_OPTION_NON_COMPLIANT')}</p>
        </div>
        <div class="asset-actions">
          <Switch bind:checked={data.noncompliant} />
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .document-edit-form {
    .section {
      &:first-child {
        margin-top: var(--margin-base);
      }

      + .section {
        margin-top: var(--margin-xlarge);
      }

      .heading {
        font-size: var(--font-size-large);
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }
    }


    .copy-field {
      position: relative;

      :global(.rule) {
        position: absolute;
        right: 5px;
        top: 5px;
      }

      &.invalid {
        :global(.rule) {
          display: none;
        }
      }
    }

    :global(.invalid-feedback) {
      color: var(--color-red-500);
      font-size: var(--font-size-medium);
      margin-top: var(--margin-base);
    }

    .asset-details {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      padding: var(--padding-small) var(--padding-base);

      .asset-item {
        display: flex;
        padding: var(--padding-large);

        + .asset-item {
          border-top: 1px solid var(--border-color);
        }

        .file,
        .document {
          display: flex;
          flex: 1;

          img {
            background-size: contain !important;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius-base);
            display: block;
            height: 120px;
            width: auto;
          }
        }
        .asset-info {
          align-items: center;
          flex: 1;

          b {
            color: var(--color-gray-900);
          }

          small {
            color: var(--color-gray-600);
            display: block;
            margin-top: var(--margin-xsmall);
          }
        }

        .asset-actions {
          align-items: center;
          display: flex;
          gap: 10px;
        }
      }
    }
  }

  @media only screen and (max-width: 600px) {
    .document-edit-form {
      .asset-details {
        .asset-item {
          display: flex;
          flex-direction: column;
          gap: 16px;

          .file {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
        }
      }
    }
  }
</style>
